import {
  Component,
  ElementRef,
  input,
  model,
  OnDestroy,
  OnInit,
  output,
  ViewChild,
} from '@angular/core';

import { translate } from '@jsverse/transloco';
import { TabObject } from '@inmoment/the-kitchen';

import { AppService } from 'app/app.service';
import {
  ConfigurationService,
  SemantriaConfiguration,
} from 'app/providers/semantria-api/configuration.service';
import { LanguageTemplate } from 'app/providers/semantria-api/language-template.service';
import { BriefIndustryPack } from 'app/providers/semantria-api/semantria-account.service';
import { AccountLimit } from 'app/providers/semantria-api/semantria-features.service';
import { AlertService } from 'app/services/alert.service';
import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { ConfigurationGeneralOptions } from '../configuration-general/configuration-general.component';
import { ConfigVisibiltyOptions } from '../../pages/configuration-details/configuration-details.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-settings-drawer',
  templateUrl: './configuration-settings-drawer.component.html',
  styleUrls: ['./configuration-settings-drawer.component.scss'],
  standalone: false,
})
export class ConfigurationSettingsDrawerComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly open = input<boolean>();
  readonly configuration = model<SemantriaConfiguration>();
  readonly visibilityOptions = input<ConfigVisibiltyOptions>();
  readonly summaryLimit = input<AccountLimit>();
  readonly isReadOnly = input<boolean>();
  readonly hasAutoAnalysis = input<boolean>();
  readonly templateConfiguration = input<any>();
  readonly industryPacks = input<BriefIndustryPack[]>([]);
  readonly languageTemplates = input<LanguageTemplate[]>([]);

  readonly close = output<void | any>();
  @ViewChild('drawer', { read: ElementRef }) drawer: ElementRef;
  hasChanges: boolean;
  isValid = true;
  newConfigGeneralOptions: ConfigurationGeneralOptions;
  isSaving: boolean;

  public configSettingsTabs: TabObject[] = [
    { title: translate('configuration-settings-drawer-component.general'), id: 'general' },
    { title: translate('configuration-settings-drawer-component.settings'), id: 'settings' },
  ];

  activeTab = 'general';

  constructor(
    private configService: ConfigurationService,
    private alertService: AlertService
  ) {}

  ngOnInit() {
    AppService.hideBodyOverflow();
  }

  public save(): void {
    this.isSaving = true;
    const oldConfig = this.configuration();
    oldConfig.name = this.newConfigGeneralOptions.configName;
    oldConfig.notes = this.newConfigGeneralOptions.configDescription;
    oldConfig.tags = this.newConfigGeneralOptions.configTags;
    const visibilityOptions = {
      showConfigDescription: this.newConfigGeneralOptions.showConfigDescription,
      showConfigTags: this.newConfigGeneralOptions.showConfigTags,
    };
    this.configuration.set(oldConfig);

    this.configService
      .updateConfiguration(this.configuration())
      .then((res) => {
        res.tags = res.tags || [];
        res.notes = res.notes || '';
        this.close.emit({ configuration: res, visibilityOptions });
      })
      .catch((err) => {
        this.alertService.showErrorToast(
          translate('configuration-settings-drawer-component.failed-update'),
          err
        );
      })
      .finally(() => (this.isSaving = false));
  }

  public cancel(): void {
    this.close.emit(null);
  }

  onFormChange($event) {
    this.newConfigGeneralOptions = $event;
    this.hasChanges = this.checkChanges();
  }

  private checkChanges() {
    return (
      this.newConfigGeneralOptions.configName !== this.configuration().name ||
      this.newConfigGeneralOptions.configDescription !== this.configuration().notes ||
      this.newConfigGeneralOptions.showConfigDescription !==
        this.visibilityOptions().showConfigDescription ||
      this.newConfigGeneralOptions.showConfigTags !== this.visibilityOptions().showConfigTags ||
      this.checkTagChanges()
    );
  }

  private checkTagChanges() {
    const config = this.configuration();
    const noTagChange =
      this.newConfigGeneralOptions.configTags.length === this.configuration()?.tags.length &&
      this.newConfigGeneralOptions.configTags.every((element, index) => {
        return element === config.tags[index];
      });
    return !noTagChange;
  }

  ngOnDestroy() {
    AppService.showBodyOverflow();
  }

  onConfigurationChange($event: SemantriaConfiguration) {
    this.configuration.set($event);
  }
}
